import { Container, Graphics, Text, TextStyle, Assets, Sprite } from 'pixi.js';
import { COLORS } from '../config/Colors';

export class LoadingScreen extends Container {
  private background: Graphics;
  private progressBar: Graphics;
  private progressFill: Graphics;
  private titleText!: Text;
  private loadingText!: Text;
  private percentText!: Text;
  private logoSprite: Sprite | null = null;
  
  private onComplete?: () => void;
  
  constructor() {
    super();
    
    this.background = new Graphics();
    this.progressBar = new Graphics();
    this.progressFill = new Graphics();
    
    this.createBackground();
    this.createLogo();
    this.createTitle();
    this.createProgressBar();
    this.createLoadingText();
    
    this.zIndex = 1000;
  }
  
  private async createLogo(): Promise<void> {
    try {
      const logoTexture = await Assets.load('/assets/ui-2/logo.png');
      this.logoSprite = new Sprite(logoTexture);
      
      // Scale logo to fit nicely (original might be large)
      const maxHeight = 300;
      const scale = maxHeight / logoTexture.height;
      this.logoSprite.scale.set(scale);
      
      this.logoSprite.anchor.set(0.5);
      this.logoSprite.x = 0;
      this.logoSprite.y = -280;
      this.logoSprite.zIndex = 1;
      
      this.addChild(this.logoSprite);
      console.log('✓ Logo loaded and added to loading screen');
    } catch (error) {
      console.error('Failed to load logo:', error);
      // Continue without logo if it fails to load
    }
  }
  
  private createBackground(): void {
    // Very dark brown background
    this.background.rect(-384, -512, 768, 1024);
    this.background.fill({ color: 0x1A0F0A });
    this.addChild(this.background);
    
    // Elegant gradient overlay
    const gradient = new Graphics();
    for (let i = 0; i < 50; i++) {
      const ratio = i / 50;
      const alpha = 0.05 + ratio * 0.15;
      gradient.rect(-384, -512 + i * 20.48, 768, 20.48);
      gradient.fill({ color: 0x3E2723, alpha: alpha });
    }
    this.addChild(gradient);
    
    // Decorative border frame
    const frame = new Graphics();
    frame.roundRect(-370, -498, 740, 996, 20);
    frame.stroke({ color: 0xFFD700, width: 3, alpha: 0.6 });
    this.addChild(frame);
    
    // Inner decorative border
    const innerFrame = new Graphics();
    innerFrame.roundRect(-360, -488, 720, 976, 15);
    innerFrame.stroke({ color: 0xDAA520, width: 1, alpha: 0.4 });
    this.addChild(innerFrame);
    
    // Top decorative line
    const topLine = new Graphics();
    topLine.rect(-300, -400, 600, 2);
    topLine.fill({ color: 0xFFD700, alpha: 0.3 });
    this.addChild(topLine);
    
    // Bottom decorative line
    const bottomLine = new Graphics();
    bottomLine.rect(-300, 350, 600, 2);
    bottomLine.fill({ color: 0xFFD700, alpha: 0.3 });
    this.addChild(bottomLine);
  }
  
  private createTitle(): void {
    // Title text removed - only showing logo
  }
  
  private createProgressBar(): void {
    const barWidth = 500;
    const barHeight = 35;
    const barX = -barWidth / 2;
    const barY = 50;
    
    // Decorative frame around progress bar
    const frame = new Graphics();
    frame.roundRect(barX - 10, barY - 10, barWidth + 20, barHeight + 20, 12);
    frame.stroke({ color: 0xFFD700, width: 2, alpha: 0.5 });
    this.addChild(frame);
    
    // Background bar with gradient effect
    this.progressBar.roundRect(barX, barY, barWidth, barHeight, 8);
    this.progressBar.fill({ color: 0x2E1810 });
    this.progressBar.stroke({ color: 0xDAA520, width: 2 });
    this.addChild(this.progressBar);
    
    // Progress fill (starts empty)
    this.progressFill.roundRect(barX + 2, barY + 2, 0, barHeight - 4, 6);
    this.progressFill.fill({ color: 0xFFD700 });
    this.addChild(this.progressFill);
    
    // Percentage text
    const percentStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 28,
      fontWeight: 'bold',
      fill: 0xFFFF99,
      stroke: { color: 0x2E1810, width: 3 }
    });
    
    this.percentText = new Text({ text: '0%', style: percentStyle });
    this.percentText.anchor.set(0.5);
    this.percentText.x = 0;
    this.percentText.y = barY + barHeight + 40;
    this.addChild(this.percentText);
  }
  
  private createLoadingText(): void {
    const loadingStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 22,
      fill: 0xE0C68A,
      stroke: { color: 0x2E1810, width: 1 }
    });
    
    this.loadingText = new Text({ text: 'Loading assets...', style: loadingStyle });
    this.loadingText.anchor.set(0.5);
    this.loadingText.x = 0;
    this.loadingText.y = 180;
    this.addChild(this.loadingText);
  }
  
  public updateProgress(progress: number, message?: string): void {
    const barWidth = 500;
    const maxWidth = barWidth - 4;
    const width = (progress / 100) * maxWidth;
    
    // Update progress bar
    this.progressFill.clear();
    if (width > 0) {
      this.progressFill.roundRect(-barWidth/2 + 2, 52, width, 26, 6);
      this.progressFill.fill({ color: COLORS.PRIMARY });
    }
    
    // Update percentage
    this.percentText.text = `${Math.floor(progress)}%`;
    
    // Update message
    if (message) {
      this.loadingText.text = message;
    }
  }
  
  public complete(callback?: () => void): void {
    this.onComplete = callback;
    
    // Animate to 100%
    this.updateProgress(100, 'Complete!');
    
    // Fade out after a short delay
    setTimeout(() => {
      this.fadeOut();
    }, 500);
  }
  
  private fadeOut(): void {
    const fadeOutDuration = 500;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / fadeOutDuration, 1);
      
      this.alpha = 1 - progress;
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.visible = false;
        if (this.onComplete) {
          this.onComplete();
        }
      }
    };
    
    animate();
  }
  
  public setLoadingAssets(): void {
    this.updateProgress(10, 'Loading game assets...');
  }
  
  public setCreatingReels(): void {
    this.updateProgress(50, 'Creating reels...');
  }
  
  public setInitializing(): void {
    this.updateProgress(80, 'Initializing game...');
  }
  
  public setComplete(): void {
    this.updateProgress(100, 'Ready!');
  }
}